/**
 * Сверка задач с «Напоминаниями» устройства.
 *
 * Отдельно от календаря, а не внутри него. Напоминание — это дело со сроком, у
 * него нет ни длительности, ни места в сетке дня, зато есть галочка
 * «выполнено». Втискивать его в правила встреч значило бы выдумывать ему
 * длительность и час начала, которых человек не задавал.
 *
 * ## Что синхронизируется
 *
 * Название, заметка, срок и отметка о выполнении. Закрыли дело здесь —
 * галочка появляется в «Напоминаниях», закрыли там — задача закрывается
 * здесь. Именно ради этого всё и нужно: иначе человек ведёт два списка и в
 * обоих не уверен.
 *
 * ## Чего здесь намеренно нет
 *
 * Новые задачи Kairo в «Напоминания» не уезжают. У задачи со временем уже
 * есть свой путь наружу — календарь, а сваливать туда же весь список дел
 * значило бы засорить чужое приложение сотней записей, о которых человек не
 * просил. Уезжают только правки тех задач, что пришли оттуда сами.
 */

import { tasksRepo } from '@/entities/task/model/repository';
import { db } from '@/shared/db/database';
import type { Task } from '@/shared/db/schema';

import { fingerprintOf, planReminderSync, type ReminderAction } from './merge-reminders';
import { type RemindersPort, systemReminders } from './reminders';
import { readCalendarSyncSettings, writeCalendarSyncState } from './settings';

export interface ReminderReport {
  /** Сколько напоминаний стало задачами. */
  pulled: number;
  /** Сколько правок уехало обратно. */
  pushed: number;
  /** Сколько задач убрано вслед за исчезнувшими напоминаниями. */
  removed: number;
}

export const EMPTY_REMINDER_REPORT: ReminderReport = { pulled: 0, pushed: 0, removed: 0 };

/**
 * Сверяет задачи с «Напоминаниями».
 *
 * `null` — сверять нечего: устройство напоминаний не знает, синхронизация
 * выключена или не выбран ни один список. Это обычное состояние, а не сбой.
 */
export async function syncReminders(
  userId: string,
  options: { port?: RemindersPort } = {},
): Promise<ReminderReport | null> {
  const port = options.port ?? systemReminders();

  const settings = await readCalendarSyncSettings(userId);
  if (!settings.enabled || settings.reminderLists.length === 0) return null;
  if (!port.available() || (await port.checkAccess()) !== 'granted') return null;

  const local = await linkedTasks(userId);
  const external = await port.listReminders(settings.reminderLists);

  const { actions } = planReminderSync({ local, external });

  const report = { ...EMPTY_REMINDER_REPORT };
  for (const action of actions) await apply(action, settings.sphereId, port, report);

  await writeCalendarSyncState(userId, { at: Date.now() });
  return report;
}

/**
 * Задачи, пришедшие из «Напоминаний».
 *
 * Только они: остальные задачи туда не отправляются, и тащить их в сверку
 * значило бы сравнивать список дел с чужим списком без всякой цели.
 */
async function linkedTasks(userId: string): Promise<Task[]> {
  const rows = await db.tasks.where('reminderId').notEqual('').toArray();
  return rows.filter((task) => task.userId === userId);
}

async function apply(
  action: ReminderAction,
  sphereId: Task['sphereId'],
  port: RemindersPort,
  report: ReminderReport,
): Promise<void> {
  try {
    switch (action.kind) {
      case 'createTask': {
        const { external } = action;

        // Защита от двойника: напоминание могло уже стать задачей, которую
        // человек передвинул или закрыл, — заводить вторую нельзя.
        const existing = await db.tasks.where('reminderId').equals(external.id).first();
        if (existing) return;

        await tasksRepo.create({
          title: external.title,
          notes: external.notes,
          sphereId,
          goalId: null,
          status: external.completed ? 'done' : 'todo',
          priority: 'normal',
          // Напоминание не говорит, сколько дело займёт. Полчаса — честная
          // заглушка: её видно в бюджете дня и легко поправить.
          estimateMin: 30,
          // Срок — это «когда надо», а не «когда делаю». День исполнения
          // человек выбирает сам, поэтому в сетку дня напоминание не встаёт.
          plannedDate: null,
          plannedStartMin: null,
          dueDate: external.dueDate,
          dueMin: external.dueMin,
          completedAt: external.completed ? Date.now() : null,
          rolloverCount: 0,
          originalDate: null,
          habitId: null,
          position: 0,

          reminderId: external.id,
          reminderListId: external.listId,
          reminderHash: fingerprintOf(external),
        });
        report.pulled += 1;
        return;
      }

      case 'updateTask': {
        const { fields } = action;

        await tasksRepo.update(action.local.id, {
          title: fields.title,
          notes: fields.notes,
          dueDate: fields.dueDate,
          dueMin: fields.dueMin,
          status: fields.completed ? 'done' : 'todo',
          completedAt: fields.completed ? (action.local.completedAt ?? Date.now()) : null,
          reminderHash: fingerprintOf(fields),
        });
        report.pulled += 1;
        return;
      }

      case 'updateReminder': {
        if (action.local.reminderId === null) return;

        await port.updateReminder(action.local.reminderId, action.fields);
        await tasksRepo.update(action.local.id, { reminderHash: fingerprintOf(action.fields) });
        report.pushed += 1;
        return;
      }

      case 'deleteTask': {
        await tasksRepo.remove(action.local.id);
        report.removed += 1;
        return;
      }
    }
  } catch {
    /*
      Одно неудачное действие не должно ронять сверку целиком: напоминание
      могли удалить прямо сейчас, доступ — отозвать. Отпечаток при неудаче не
      обновляется, поэтому следующая сверка попробует снова.
    */
  }
}
